import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { useCurrency } from '@/context/SettingsContext';
import axios from 'axios';
import {
  ChartLine,
  CurrencyDollar,
  ShoppingCart,
  TrendUp,
  Clock,
  ArrowUp,
  ArrowDown,
  CalendarBlank,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
export const ReportsPage = () => {
  const { currentOutlet } = useApp();
  const sym = useCurrency();
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: weekAgo, end: today });
  const [salesData, setSalesData] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReportData = useCallback(async () => {
    if (!currentOutlet) return;
    setLoading(true);
    try {
      const q = `outlet_id=${currentOutlet.id}&start_date=${range.start}T00:00:00&end_date=${range.end}T23:59:59`;
      const [salesRes, topItemsRes, hourlyRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/sales?${q}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/top-items?${q}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/hourly?${q}`, { withCredentials: true }),
      ]);

      setSalesData(salesRes.data);
      setTopItems(topItemsRes.data);
      setHourlyData(hourlyRes.data.map(d => ({
        ...d,
        hour: `${d.hour}:00`,
      })));
    } catch (e) {
      console.error('Error fetching report data:', e);
    } finally {
      setLoading(false);
    }
  }, [currentOutlet, range]);

  useEffect(() => { fetchReportData(); }, [fetchReportData]);

  const setPreset = (days) => {
    const e = new Date().toISOString().slice(0, 10);
    const s = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);
    setRange({ start: s, end: e });
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to view reports</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Reports & Analytics</h1>
          <p className="text-muted-foreground mt-1">
            {currentOutlet?.name} · {range.start} → {range.end}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end" data-testid="report-date-filters">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={range.start} max={range.end} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="report-start-date" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={range.end} min={range.start} max={today} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="report-end-date" />
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setPreset(1)} data-testid="report-preset-today">Today</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(7)} data-testid="report-preset-7d">7d</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(30)} data-testid="report-preset-30d">30d</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(90)} data-testid="report-preset-90d">90d</Button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sales</p>
                <p className="text-3xl font-heading font-bold mt-2">
                  {sym}{(salesData?.total_sales || 0).toFixed(2)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-accent-green/10">
                <CurrencyDollar size={24} className="text-accent-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Orders</p>
                <p className="text-3xl font-heading font-bold mt-2">
                  {salesData?.total_orders || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <ShoppingCart size={24} className="text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Order Value</p>
                <p className="text-3xl font-heading font-bold mt-2">
                  ${(salesData?.avg_order_value || 0).toFixed(2)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-accent-yellow/10">
                <TrendUp size={24} className="text-accent-yellow" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Tax</p>
                <p className="text-3xl font-heading font-bold mt-2">
                  ${(salesData?.total_tax || 0).toFixed(2)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-surface">
                <ChartLine size={24} className="text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Sales Chart */}
        <Card className="border border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-heading text-lg">Hourly Sales</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="sales" fill="#002FA7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hourly Orders Chart */}
        <Card className="border border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-heading text-lg">Hourly Orders</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="orders" 
                    stroke="#34C759" 
                    strokeWidth={2}
                    dot={{ fill: '#34C759', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Items */}
      <Card className="border border-border">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-heading text-lg">Top Selling Items Today</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {topItems.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                No sales data yet
              </div>
            ) : (
              topItems.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-heading font-bold text-primary">
                      {index + 1}
                    </span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{item.quantity} sold</p>
                    <p className="text-sm text-muted-foreground">${item.revenue.toFixed(2)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
